/**
 * @file plans.js
 * @description Canonical subscription plan registry. Single source of truth for
 *   plan definitions, feature lists, and pricing. Prices are overridden at runtime
 *   by admin-configurable values from configService. Do not hardcode prices elsewhere.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/plans
 */
// ─── ABA cases — Canonical Plan Registry ──────────────────────────────
//
// SINGLE SOURCE OF TRUTH for all subscription plan data.
//
// Default values are defined here. Prices and limits are overridden at
// runtime by admin-configurable values from platform_config via
// configService.js. Use getPlans() for the live config-aware values.
//
// Do NOT hardcode prices elsewhere. Import from this file.
//
// Plan-card bullets are NOT stored here: they are derived from the comparison
// rows below (getPlanCardFeatures), so the card and the table cannot drift.
// ──────────────────────────────────────────────────────────────────────────

import { getCachedConfig } from "./configService";
import { proTeamLogins, isWorkforceNamed } from "./entitlements";

/**
 * @description Initial Promo on the Starter plan: the first
 *   `max_free_cases_agency` case slots are free through `starter_promo_end_date`.
 *   Every slot — including those first ones — is really $X/mo (see
 *   extra_case_slot_price_starter); the promo is a temporary waiver, not a
 *   standing free tier. Victor's 2026-09-23 decision.
 *
 *   `starter_promo_end_date` (platform_config) is the ONLY source of truth
 *   for the cutover date — the same config `get_starter_free_cap()` reads
 *   live in Postgres to actually enforce it (see migration
 *   starter_promo_date_gate). Every page that shows Starter promo copy
 *   should call this instead of hardcoding "December 31, 2026" or similar —
 *   that's exactly the drift bug this function exists to prevent.
 * @returns {{
 *   endDate: Date, resumeDate: Date,
 *   endDateStr: string, resumeDateStr: string,
 *   endDateStrEs: string, resumeDateStrEs: string,
 *   endDateShortStr: string, endDateShortStrEs: string,
 *   isPromoActive: boolean,
 * }}
 */
export function getStarterPromoDates() {
  const raw = getCachedConfig("starter_promo_end_date") || "2026-12-31";
  const tz = getCachedConfig("starter_promo_end_tz") || "America/New_York";
  const endDate = new Date(`${raw}T00:00:00Z`);
  const resumeDate = new Date(endDate.getTime() + 86_400_000); // +1 day — promo end date is inclusive
  const fmt = (d, locale) => d.toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
  const fmtShort = (d, locale) => d.toLocaleDateString(locale, { month: "short", year: "numeric", timeZone: "UTC" });
  // The instant the promo really ends: midnight (00:00) in the promo time zone at the start of the day after the
  // end date. Mirrors the DB engine starter_promo_end_at() (migration 20261123090000), which the server enforces.
  const endInstant = zonedMidnight(resumeDate, tz);
  return {
    endDate,
    resumeDate,
    endInstant,
    endTz: tz,
    endDateStr: fmt(endDate, "en-US"),
    resumeDateStr: fmt(resumeDate, "en-US"),
    endDateStrEs: fmt(endDate, "es-ES"),
    resumeDateStrEs: fmt(resumeDate, "es-ES"),
    // Compact "Dec 2026" form for tight spaces (e.g. the hero subtitle).
    endDateShortStr: fmtShort(endDate, "en-US"),
    endDateShortStrEs: fmtShort(endDate, "es-ES"),
    // Live client-side check against the SAME config the backend enforces (display only; the server decides).
    isPromoActive: Date.now() < endInstant.getTime(),
  };
}

/**
 * The UTC instant at which a calendar date (taken from the UTC fields of `day`) starts in `tz`.
 * Pure Intl arithmetic, no library. Falls back to UTC midnight for an unknown zone.
 * @param {Date} day a date whose UTC year/month/day name the calendar day
 * @param {string} tz IANA zone, e.g. America/New_York
 * @returns {Date}
 */
export function zonedMidnight(day, tz) {
  const utcMidnight = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate());
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric",
    });
    // Wall clock of instant t in the zone, read back as if it were UTC.
    const wall = (t) => {
      const parts = fmt.formatToParts(new Date(t));
      const g = (type) => Number(parts.find((x) => x.type === type)?.value);
      return Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute"), g("second"));
    };
    let t = utcMidnight;
    for (let i = 0; i < 3; i += 1) t = utcMidnight - (wall(t) - t); // converges in 2 steps, even across DST
    return new Date(t);
  } catch {
    return new Date(utcMidnight);
  }
}

// ── Static plan definitions (defaults — overridden by config at runtime) ─────

export const PLANS = [
  {
    id:         "starter",
    label:      "Starter",
    mo:         29,
    annualMo:   0,
    cases:      2,       // free promo openings while the promo runs (0 after); see getPlans()
    included:   1,       // openings the paid Starter price includes (plan_starter_cases)
    popular:    false,
    creditsMonthly: 0,
    analytics:  "basic",
    tagline:    "Get started and post your first openings",
  },
  {
    id:         "pro",
    label:      "Pro",
    mo:         179,
    annualMo:   150,
    cases:      10,
    popular:    true,
    creditsMonthly: 0,
    analytics:  "full",
    tagline:    "Fill openings faster with full workflow tools and analytics",
  },
];

// Hibernate is a special operational state — not purchasable, not shown in
// plan comparison cards, but valid in plan_history and agency_profiles.
export const HIBERNATE_CONFIG = {
  id:       "hibernate",
  label:    "Paused",
  mo:       0,
  annualMo: 0,
  cases:    0,
  creditsMonthly: 0,
  analytics: "basic",
};

/**
 * @description Returns the PLANS array with prices and limits overridden by
 *   current admin-configurable values from the platform_config cache.
 *   Safe to call synchronously — reads from the in-memory config cache.
 * @returns {Object[]} Plan definitions with live prices and slot counts
 */
export function getPlans() {
  const pMo    = Number(getCachedConfig("plan_pro_monthly"))  || 179;
  const pAn    = Number(getCachedConfig("plan_pro_annual"))   || 150;
  const pCases = Number(getCachedConfig("plan_pro_cases"))    || 10;
  // Legacy — Pro no longer bundles monthly credits by default. The config
  // remains as an admin escape hatch; if it's set >0 we still show the
  // included-credits line so the marketing copy reflects what actually fires.
  const pCred  = Number(getCachedConfig("plan_pro_credits"))  || 0;
  // Date-gated so every consumer of getPlans() — not just the marketing
  // pages that explicitly check isPromoActive for the promo badge — gets
  // the real current free-case count. Mirrors get_starter_free_cap() in
  // Postgres exactly: same config key, same cutover date, same fallback.
  const maxFree = getStarterPromoDates().isPromoActive
    ? Number(getCachedConfig("max_free_cases_agency")) || 2
    : Number(getCachedConfig("starter_promo_post_cases")) || 0;

  return PLANS.map(p => {
    if (p.id === "starter") {
      return {
        ...p,
        cases: maxFree,
        // Starter IS the extra-openings subscription: one price per opening (quantity N = Starter with its first
        // opening included plus N-1 extra openings), so the plan price and the extra price are the same number.
        mo: Number(getCachedConfig("extra_case_slot_price_starter")) || 29,
        included: Number(getCachedConfig("plan_starter_cases")) || 1,
        extraMo: Number(getCachedConfig("extra_case_slot_price_starter")) || 29,
      };
    }
    if (p.id === "pro") return { ...p, mo: pMo, annualMo: pAn, cases: pCases, creditsMonthly: pCred, extraMo: Number(getCachedConfig("extra_case_slot_price_pro")) || 15 };
    return p;
  });
}

// Lookup map: plan id → config object (includes hibernate)
export const PLAN_BY_ID = Object.fromEntries(
  [...PLANS, HIBERNATE_CONFIG].map(p => [p.id, p])
);

// Billing math shortcut: plan id → monthly dollar value. The plan "starter" has no plan fee: Starter revenue is the
// extra-openings subscription (lib/mrr slotMonthly), counted per agency.
export const PLAN_MRR = {
  ...Object.fromEntries([...PLANS, HIBERNATE_CONFIG].map(p => [p.id, p.mo])),
  starter: 0,
};

// ─── Plan comparison — sectioned, i18n-ready, single source of truth ────────
// This is what BOTH the marketing "For Agencies" page and the agency-side
// Billing page render. Keeping it here prevents drift between the marketing
// promise and the in-product comparison.
//
// Pass `t` from useTranslation() into getComparisonSections() so labels render
// in the active language. All keys live under plans.compare.* in en/es.json.
const buildComparisonSections = (t) => [
  {
    title: t('plans.compare.sectionPosting'),
    rows: [
      // Starter cell is computed dynamically — see getComparisonSections()
      { id: "active_listings",  label: t('plans.compare.rowActiveListings') },
      // Extra-slot price is plan-differentiated — Pro slots cost less, so growing
      // agencies are better off upgrading than stacking pricey Starter slots.
      { id: "extra_slot",       label: t('plans.compare.rowExtraSlot') },
      // ABAcases Workforce is in both plans, but the row only exists once Workforce is released (workforce_enabled).
      // The cells say how many people each plan can track in Workforce (platform_config workforce_max_people_starter / _pro).
      ...(isWorkforceNamed() ? [{ label: t('plans.compare.rowWorkforce'),
        starter: t('plans.compare.cellWorkforcePeople', { n: workforceMax("starter") }),
        pro: t('plans.compare.cellWorkforcePeople', { n: workforceMax("pro") }) }] : []),
      // "Mark a case urgent" is NOT a plan difference: any plan can do it by
      // spending boost credits (urgency_badge), so it lives in the boosts
      // section, which this table filters out.
    ],
  },
  {
    title: t('plans.compare.sectionFinding'),
    rows: [
      { label: t('plans.compare.rowSideBySide'),    starter: true, pro: true },
      { label: t('plans.compare.rowHiringStages'),  starter: true, pro: true, core: true, short: t('plans.card.pipeline') },
      { label: t('plans.compare.rowMessage'),       starter: true, pro: true },
      // Paid direct outreach from the Provider Pool is Pro only.
      { label: t('plans.compare.rowMessageFirst'),  starter: false, pro: true, highlight: true, short: t('plans.card.messageFirst') },
      // Team logins (owner 2026-10-20): Starter has the admin login only; Pro adds teammates (lib/entitlements).
      { label: t('plans.compare.rowTeamLogins'), starter: t('plans.compare.cellOneLogin'),
        pro: t('plans.compare.cellTeamLogins', { count: proTeamLogins(getCachedConfig), others: Math.max(1, proTeamLogins(getCachedConfig) - 1) }),
        highlight: true, short: t('plans.card.teamLogins', { count: proTeamLogins(getCachedConfig), others: Math.max(1, proTeamLogins(getCachedConfig) - 1) }) },
      { label: t('plans.compare.rowSchedule'),      starter: true, pro: true },
      { label: t('plans.compare.rowPrivateNotes'),  starter: true, pro: true, core: true, short: t('plans.card.notes') },
    ],
  },
  {
    title: t('plans.compare.sectionTrust'),
    rows: [
      { label: t('plans.compare.rowCredentials'),   starter: true, pro: true },
      { label: t('plans.compare.rowNpi'),           starter: true, pro: true, core: true, short: t('plans.card.npi') },
      { label: t('plans.compare.rowBacb'),          starter: true, pro: true },
    ],
  },
  {
    title: t('plans.compare.sectionAlerts'),
    rows: [
      { label: t('plans.compare.rowNotifyApply'),   starter: true, pro: true },
      { label: t('plans.compare.rowStaleReminder'), starter: true, pro: true },
      { label: t('plans.compare.rowNotifHistory'),  starter: t('plans.compare.cell30Days'), pro: t('plans.compare.cellFullHistory'), highlight: true, short: t('plans.card.fullHistory') },
    ],
  },
  {
    title: t('plans.compare.sectionReporting'),
    rows: [
      { label: t('plans.compare.rowInsightsCockpit'), starter: true, pro: true, core: true, short: t('plans.card.basicAnalytics') },
      { label: t('plans.compare.rowApplicantCount'),  starter: true, pro: true },
      { label: t('plans.compare.rowDownloadData'),    starter: true, pro: true },
      { label: t('plans.compare.rowFillTime'),        starter: t('plans.compare.cellLimited'), pro: true },
      { label: t('plans.compare.rowDropOff'),         starter: false, pro: true },
      { label: t('plans.compare.rowFillRate'),        starter: false, pro: true, highlight: true, short: t('plans.card.fullInsights') },
      { label: t('plans.compare.rowSpendRoi'),        starter: false, pro: true },
      { label: t('plans.compare.rowCsvUpload'),       starter: false, pro: true, highlight: true, short: t('plans.card.bulkCsv') },
    ],
  },
  {
    title: "Boosting & Visibility",
    dynamic: "boosts",
    rows: [],
  },
  {
    title: t('plans.compare.sectionHistory'),
    rows: [
      { label: t('plans.compare.rowPastCases'),        starter: true, pro: true },
      { label: t('plans.compare.rowApplicantHistory'), starter: true, pro: true },
    ],
  },
];

/** People each plan can track in ABAcases Workforce (Admin settings; display only, the server enforces the cap). */
export function workforceMax(planId) {
  return planId === "pro"
    ? Number(getCachedConfig("workforce_max_people_pro")) || 500
    : Number(getCachedConfig("workforce_max_people_starter")) || 15;
}

/** The card bullet "ABAcases Workforce: up to N people"; empty until Workforce is released (one switch). */
function workforceBullet(planId, t) {
  return isWorkforceNamed() ? [t('plans.card.workforcePeople', { n: workforceMax(planId) })] : [];
}

/**
 * @description Bullets for a plan card in the Billing drawer, DERIVED from the
 *   comparison rows above (single source of truth with the Compare Features
 *   table). Rules:
 *     - Pro: rows flagged `highlight` where Pro has more than Starter (Starter
 *       false, or a smaller value such as 30 days vs full history), so the
 *       card only lists real differences. `lead` is the "Everything in
 *       Starter, plus" line that stops Pro looking smaller than Starter.
 *     - Starter: only the few rows flagged `core` (a short list on purpose).
 *   The active-case count is not a bullet: the card shows it once under the price.
 * @param {"starter"|"pro"} planId
 * @param {Object} ctx
 * @param {Function} ctx.t  i18n translate fn
 * @returns {{ lead: string|null, items: string[] }}
 */
export function getPlanCardFeatures(planId, { t = (k) => k } = {}) {
  const rows = buildComparisonSections(t).flatMap(section => section.rows);
  const text = (r) => r.short || r.label;
  if (planId === "pro") {
    const credits = Number(getCachedConfig("plan_pro_credits")) || 0; // legacy escape hatch, usually 0
    const items = rows.filter(r => r.highlight && r.pro && r.starter !== true).map(text);
    return {
      lead: t('plans.card.everythingInStarter'),
      items: [...(credits > 0 ? [t('plans.card.monthlyCredits', { count: credits })] : []), ...items, ...workforceBullet("pro", t)],
    };
  }
  return { lead: null, items: [...rows.filter(r => r.core && r.starter === true).map(text), ...workforceBullet("starter", t)] };
}

/**
 * @description Returns the comparison sections with all live-config values
 *   substituted. The marketing page and the Billing page both call this so
 *   they stay perfectly in sync.
 *
 * @param {Object} ctx
 * @param {number} ctx.starterIncluded — `plan_starter_cases` value (openings paid Starter includes)
 * @param {number} ctx.proCases       — `plan_pro_cases` value
 * @param {Object} ctx.boostProducts  — output of getBoostProducts(cfg)
 */
export function getComparisonSections({
  t = (k) => k,
  starterIncluded = Number(getCachedConfig("plan_starter_cases")) || 1,
  proCases     = 10,
  boostProducts = {},
  slotPriceStarter = Number(getCachedConfig("extra_case_slot_price_starter")) || 29,
  slotPricePro     = Number(getCachedConfig("extra_case_slot_price_pro")) || 15,
} = {}) {
  const cr = (n) => `${n} cr`;
  const bp = boostProducts || {};

  // "Active openings included" cell: what paid Starter includes (the free period's extra openings are not a plan feature).
  const starterListingCell = t('plans.compare.starterListingCell', { count: starterIncluded });
  const proListingCell = t('plans.compare.proListingCell', { count: proCases });

  return buildComparisonSections(t)
    // Boosts are usage-based and decoupled from plan tier — every row in this
    // section would be identical across Starter and Pro, which makes it noise
    // in a Compare Features table. The signup grant is fired one-time per
    // agency by the trg_grant_signup_boost trigger on agency_profiles.profile_complete
    // and is the same for both plans. Boost pricing lives in the per-case
    // BoostPanel where it's actionable; this comparison table stays focused
    // on plan-level differences.
    .filter(section => section.dynamic !== "boosts")
    .map(section => {
    if (section.dynamic === "boosts") {
      // Boost credits are sold as packs to every plan — no monthly bundle.
      // New agencies get a one-time onboarding grant (configurable) so they
      // can try a boost on their first case.
      const signupGrant = Number(getCachedConfig("boost_signup_grant_credits")) || 0;
      const monthlyPro  = Number(getCachedConfig("plan_pro_credits")) || 0; // legacy — usually 0
      const welcomeRow  = signupGrant > 0
        ? { label: "Welcome boost credit when you sign up", starter: `${signupGrant} included`, pro: `${signupGrant} included` }
        : null;
      const legacyRow   = monthlyPro > 0
        ? { label: "Boost credits included every month", starter: "Buy as needed", pro: `${monthlyPro} included` }
        : null;

      return {
        ...section,
        rows: [
          ...(welcomeRow ? [welcomeRow] : []),
          ...(legacyRow  ? [legacyRow]  : []),
          { label: "Buy boost credits anytime",                       starter: true,              pro: true },
          { label: "Spotlight a case at the top of search results",   starter: cr(bp.spotlight?.credits ?? 3),     pro: cr(bp.spotlight?.credits ?? 3) },
          { label: "Reach Blast: push a case to more providers",    starter: cr(bp.reach_blast?.credits ?? 2),   pro: cr(bp.reach_blast?.credits ?? 2) },
          { label: "Mark Urgent: visible badge until case fills",    starter: cr(bp.urgency_badge?.credits ?? 2), pro: cr(bp.urgency_badge?.credits ?? 2) },
          { label: "Fast Fill Bundle (Spotlight + Reach + Urgent)",   starter: cr(bp.bundle?.credits ?? 6),        pro: cr(bp.bundle?.credits ?? 6) },
          { label: "Spotlight slots are limited per city: exclusive top placement while active", starter: "✦ Limited", pro: "✦ Limited" },
        ],
      };
    }
    return {
      ...section,
      rows: section.rows.map(r => {
        if (r.id === "active_listings") {
          return { label: r.label, starter: starterListingCell, pro: proListingCell };
        }
        if (r.id === "extra_slot") {
          return { label: r.label, starter: `$${slotPriceStarter}/mo each`, pro: `$${slotPricePro}/mo each` };
        }
        return r;
      }),
    };
  });
}
